import DashboardDefaultChartEmptyState from "@components/EmptyStates/DashboardDefaultChartEmptyState";
import DashboardSplitChartEmptyState from "@components/EmptyStates/DashboardSplitChartEmptyState";
import { ToggletipText, ToggletipTextWrapper } from "src/style";
import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import {
  sendEventChartUsed,
  sendEventEmptyStateClick,
} from "@services/dashboardAnalyticEventService";
import {
  dashboardChartEmptyStatesTypes,
  dashboardChartFilterAndActionTypes,
} from "@utils/constants/dashboard/analyticDashboardEvents";
import {
  DASHBOARD_CASE_TYPES,
  DASHBOARD_CHART_TYPES,
  DASHBOARD_RANGE_TYPE_KEYS,
  DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
} from "@utils/constants/dashboard/dashboardModuleConstants";

import DashboardChartFilters from "@components/DashboardChartFilters";
import DashboardChartCaseTypeFilter from "@components/DashboardChartFilters/DashboardChartCaseTypeFilter";
import DashboardChartRangeFilter from "@components/DashboardChartFilters/DashboardChartRangeFilter";
import DashboardChartResponsibleFilter from "@components/DashboardChartFilters/DashboardChartResponsibleFilter";
import DashboardChartWrapper from "@components/DashboardChartWrapper";
import { QueryLoaderWrapper } from "@components/Loaders";
import LawsuitUpdatesRadarChartSkeleton from "@components/Loaders/skeletons/LawsuitUpdatesRadarChartSkeleton";
import useDefaultResponsibleFilter from "@hooks/useDefaultResponsibleFilter/useDefaultResponsibleFilter";
import { getChartOptions, MINIMUM_RESPONSIBLE_NUMBER } from "./helpers";
import { DoubleColumnChartWrapper, RadarChartContainer, StyledChartTitle } from "./style";
import useLawsuitUpdatesRadarChartQuery from "./useLawsuitUpdatesRadarChartQuery";
import { CaseTypeFilter } from "@customTypes/components/filters";

const LawsuitUpdatesRadarChart = () => {
  const ALLOWED_RANGE_TYPES: DASHBOARD_RANGE_TYPE_KEYS[] = [
    "TODAY",
    "LAST_7_DAYS",
    "LAST_30_DAYS",
    "LAST_60_DAYS",
    "LAST_90_DAYS",
  ];
  const DEFAULT_RESPONSIBLE_FILTER = useDefaultResponsibleFilter(MINIMUM_RESPONSIBLE_NUMBER);

  const [responsibleFilter, setResponsibleFilter] = useState(DEFAULT_RESPONSIBLE_FILTER);
  const [caseTypeFilter, setCaseTypeFilter] = useState(DASHBOARD_CASE_TYPES.ALL);
  const [rangeFilter, setRangeFilter] = useState<DASHBOARD_RANGE_TYPE_KEYS>("LAST_30_DAYS");

  const [queryStatus, lawsuitUpdatesRadarChartResponse] = useLawsuitUpdatesRadarChartQuery(
    caseTypeFilter.queryParam,
    responsibleFilter,
    rangeFilter as DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES
  );

  const { users, series, noData } = lawsuitUpdatesRadarChartResponse;
  const options = getChartOptions(users);

  const setRangeFilterAndSendMixPanelEvent = (filter: DASHBOARD_RANGE_TYPE_KEYS) => {
    setRangeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_RADAR,
      dashboardChartFilterAndActionTypes.DATE_RANGE,
      filter
    );
  };

  const setCaseTypeFilterAndSendMixPanelEvent = (filter: CaseTypeFilter) => {
    setCaseTypeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_RADAR,
      dashboardChartFilterAndActionTypes.LAWSUIT_TYPE,
      filter
    );
  };

  const setResponsibleFilterAndSendMixPanelEvent = (filter: string[]) => {
    setResponsibleFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_RADAR,
      dashboardChartFilterAndActionTypes.RESPONSIBLE
    );
  };

  const shouldRenderEmptyStates = noData;

  const shouldRenderFullChartEmptyState =
    shouldRenderEmptyStates?.notEnoughUsers ||
    (shouldRenderEmptyStates?.withUpdates && shouldRenderEmptyStates?.withoutUpdates);

  const header = {
    title: "Movimentação de processos e casos por responsável",
    description:
      "Confira a quantidade de processos e casos que possuem ou não movimentação no período selecionado para analisar uma possível repriorização de esforços dos responsáveis.",
  };

  const toggleTip = {
    title: "Movimentação por responsável",
    text: (
      <ToggletipTextWrapper>
        <ToggletipText>
          O total de processos e casos de cada responsável é separado pelos que tiveram ou não
          movimentação no período.
        </ToggletipText>
        <ToggletipText>
          Uma ou mais movimentações identificam o processo ou caso como movimentado.
        </ToggletipText>
      </ToggletipTextWrapper>
    ),
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_RADAR,
        dashboardChartFilterAndActionTypes.TOGGLE_TIP_OPENED
      );
    },
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_RADAR,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043388-como-analisar-os-processos-e-casos-por-responsavel",
          "_blank"
        );
      },
    },
  };

  const FullChartEmptyState = () => {
    const openUserManagement = () => {
      sendEventEmptyStateClick(dashboardChartEmptyStatesTypes.CHECK_RESOURCES);
      window.open("#/main/admin/access/", "_self");
    };

    return (
      <DashboardDefaultChartEmptyState
        title={
          shouldRenderEmptyStates.notEnoughUsers ? (
            "Não há responsáveis suficientes para gerar o gráfico"
          ) : (
            <>
              <p>Nenhum processo ou caso com ou sem</p>
              <p>movimentação encontrado</p>
            </>
          )
        }
        subtitle={
          shouldRenderEmptyStates.notEnoughUsers
            ? "Adicione mais usuários no Astrea e filtre por pelo menos 3 responsáveis para ver o gráfico."
            : "Verifique os filtros selecionados."
        }
        chartId={DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_RADAR}
        link="https://astrea.aurum.com.br/pt-BR/articles/8043388-como-analisar-os-processos-e-casos-por-responsavel"
        buttonText={shouldRenderEmptyStates.notEnoughUsers ? "Ver Usuários" : undefined}
        buttonCallback={shouldRenderEmptyStates.notEnoughUsers ? openUserManagement : undefined}
      />
    );
  };

  const WithUpdatesRadarChart = () => (
    <DoubleColumnChartWrapper>
      <StyledChartTitle size="md" semiBold>
        Com movimentação
      </StyledChartTitle>
      {shouldRenderEmptyStates.withUpdates ? (
        <DashboardSplitChartEmptyState
          title={
            <>
              <p>Nenhum processo ou caso com</p>
              <p>movimentação encontrado</p>
            </>
          }
          subtitle="Verifique os filtros selecionados."
          chartId={DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_RADAR}
          link="https://astrea.aurum.com.br/pt-BR/articles/8043388-como-analisar-os-processos-e-casos-por-responsavel"
          mobileText="Nenhum processo ou caso com movimentação encontrado. Verifique os filtros selecionados."
        />
      ) : (
        <ReactApexChart options={options.withUpdates} series={series.withUpdates} type="radar" />
      )}
    </DoubleColumnChartWrapper>
  );

  const WithoutUpdatesRadarChart = () => (
    <DoubleColumnChartWrapper>
      <StyledChartTitle size="md" semiBold>
        Sem movimentação
      </StyledChartTitle>
      {shouldRenderEmptyStates.withoutUpdates ? (
        <DashboardSplitChartEmptyState
          title={
            <>
              <p>Nenhum processo ou caso sem</p>
              <p>movimentação encontrado</p>
            </>
          }
          subtitle="Verifique os filtros selecionados."
          chartId={DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_RADAR}
          link="https://astrea.aurum.com.br/pt-BR/articles/8043388-como-analisar-os-processos-e-casos-por-responsavel"
          mobileText="Nenhum processo ou caso com movimentação encontrado. Verifique os filtros selecionados."
        />
      ) : (
        <ReactApexChart
          options={options.withoutUpdates}
          series={series.withoutUpdates}
          type="radar"
        />
      )}
    </DoubleColumnChartWrapper>
  );
  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <DashboardChartFilters>
        <DashboardChartRangeFilter
          allowedRangeTypes={ALLOWED_RANGE_TYPES}
          selectedRange={rangeFilter}
          setRangeFunction={setRangeFilterAndSendMixPanelEvent}
        />
        <DashboardChartResponsibleFilter
          currentFilter={responsibleFilter}
          setFilter={setResponsibleFilterAndSendMixPanelEvent}
          minimumSelected={MINIMUM_RESPONSIBLE_NUMBER}
        />
        <DashboardChartCaseTypeFilter
          currentFilter={caseTypeFilter}
          setFilter={setCaseTypeFilterAndSendMixPanelEvent}
        />
      </DashboardChartFilters>

      {shouldRenderFullChartEmptyState ? (
        <FullChartEmptyState />
      ) : (
        <RadarChartContainer>
          <QueryLoaderWrapper
            queryStatus={queryStatus}
            skeletonComponent={<LawsuitUpdatesRadarChartSkeleton />}
          >
            <WithUpdatesRadarChart />
          </QueryLoaderWrapper>
          <QueryLoaderWrapper
            queryStatus={queryStatus}
            skeletonComponent={<LawsuitUpdatesRadarChartSkeleton />}
          >
            <WithoutUpdatesRadarChart />
          </QueryLoaderWrapper>
        </RadarChartContainer>
      )}
    </DashboardChartWrapper>
  );
};

export default LawsuitUpdatesRadarChart;
